import { extractFirstName, parseLeadingGreetingBoundary } from './emailPreview';
import { CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN } from './draftGuards';
import { safeGreetingFirstName } from './greetingName';

// A contact cell is a person to greet only when the ONE shared greeting rule
// can take a first name out of it. This used to be a twenty-word company stop
// list of its own, and on 2026-09-22 it passed "Not provided" — two ordinary
// tokens, no digit, no brand capitalisation — so the row counted as a confirmed
// person and the product's first outreach email opened "Hi Not,". The rule now
// lives in ./greetingName (mirrored from functions/lib/greetingName.js, ported
// from email_automation/greeting_name.py) and is the same one the backend
// applies to replies and follow-ups.
function isLikelyPersonContact(contactName) {
  return safeGreetingFirstName(contactName) !== null;
}

export function scriptUsesNameVariable(script = '') {
  return /\[(?:first\s*)?name\]|\{\{\s*(?:firstName|first_name|name)\s*\}\}|\{\s*(?:firstName|first_name|name)\s*\}/i.test(String(script || ''));
}

function normalizeRecipientIdentityPart(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

// A leading greeting whose addressee the lossless composer cannot classify
// (kind 'ambiguous') is preserved verbatim by design. That is safe for one
// recipient, but with several recipients the literal addressee would be
// delivered to every broker unchanged, because injectNameIntoScript only ever
// substitutes an explicit [NAME]-style variable. Treat that as unresolved
// recipient identity so campaign launch fails closed instead of sending one
// broker's name to all of them.
//
// Greeting strength is deliberately NOT considered. A weak greeting ('Morning
// Jordan,') hides a literal addressee just as effectively as a strong one, and
// exempting it let a fixed name reach every recipient unrefused.
//
// A body that merely opens with a weak greeting WORD is a different thing:
// 'Afternoon tours are preferred by our client.' addresses nobody, so blocking
// it would demand a [NAME] the copy has no place for. draftGuards already draws
// that line for the launch refusal, so the same pattern draws it here.
function hasUnresolvableLiteralGreeting(script) {
  const text = String(script || '').trimStart();
  if (CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN.test(text)) return false;

  const boundary = parseLeadingGreetingBoundary(text);
  return boundary?.kind === 'ambiguous';
}

export function buildContactAwareRecipientPlan({ brokerData = {}, emailToContactMap = {} } = {}) {
  const rows = Object.entries(brokerData).flatMap(([email, properties = []]) => (
    (Array.isArray(properties) ? properties : []).map((property = {}, propertyIndex) => ({
      id: `${email}-${propertyIndex}`,
      email,
      normalizedEmail: normalizeRecipientIdentityPart(email),
      property,
      propertyIndex,
      firstNameOverride: String(property.firstNameOverride || '').trim(),
      rowContactName: String(property.contact || '').trim()
    }))
  ));
  const knownContactsByEmail = new Map();
  const addKnownContact = (email, contactName) => {
    const normalizedContact = normalizeRecipientIdentityPart(contactName);
    if (!normalizedContact) return;
    if (!knownContactsByEmail.has(email)) {
      knownContactsByEmail.set(email, new Map());
    }
    const contacts = knownContactsByEmail.get(email);
    if (!contacts.has(normalizedContact)) contacts.set(normalizedContact, String(contactName).trim());
  };

  rows.forEach(row => {
    addKnownContact(row.normalizedEmail, row.rowContactName);
  });
  Object.entries(emailToContactMap).forEach(([email, contact]) => {
    addKnownContact(normalizeRecipientIdentityPart(email), contact);
  });
  const identityCounts = new Map();

  return rows.map(row => {
    const knownContacts = knownContactsByEmail.get(row.normalizedEmail) || new Map();
    const requiresContactResolution = !row.rowContactName && knownContacts.size > 1;
    const unambiguousLegacyFallback = !row.rowContactName && knownContacts.size === 1
      ? knownContacts.values().next().value
      : '';
    const resolvedContactName = row.rowContactName || unambiguousLegacyFallback || '';
    const identityContactName = resolvedContactName;
    const normalizedContact = normalizeRecipientIdentityPart(identityContactName);
    const identityKey = `${row.normalizedEmail}\u0000${normalizedContact}`;
    const contactOrdinal = identityCounts.get(identityKey) || 0;
    identityCounts.set(identityKey, contactOrdinal + 1);

    return {
      id: row.id,
      email: row.email,
      normalizedEmail: row.normalizedEmail,
      property: row.property,
      propertyIndex: row.propertyIndex,
      identityKey,
      contactOrdinal,
      scriptIndex: contactOrdinal,
      resolvedContactName,
      firstNameOverride: row.firstNameOverride,
      hasManualOverride: Boolean(row.firstNameOverride),
      hasMultipleKnownContacts: knownContacts.size > 1,
      requiresContactResolution,
      source: row.rowContactName
        ? 'leasing_contact'
        : requiresContactResolution
          ? 'ambiguous_contact'
          : resolvedContactName
            ? 'legacy_fallback'
            : 'missing'
    };
  });
}

export function buildRecipientVariableReviews({ brokerData = {}, emailToContactMap = {}, scripts = [], recipientPlan } = {}) {
  const normalizedScripts = Array.isArray(scripts) ? scripts : [];
  const plannedRecipients = Array.isArray(recipientPlan)
    ? recipientPlan
    : buildContactAwareRecipientPlan({ brokerData, emailToContactMap });
  const hasMultipleRecipientIdentities = new Set(
    plannedRecipients.map(recipient => normalizeRecipientIdentityPart(recipient.email))
  ).size > 1;

  return plannedRecipients.map(recipient => {
    const { property = {}, propertyIndex, scriptIndex, resolvedContactName } = recipient;
    const script = normalizedScripts[scriptIndex] || '';
    const contactIsConfirmed = isLikelyPersonContact(resolvedContactName);
    // A manual override goes through the same one greeting rule as the contact
    // cell. It used to be taken verbatim (report-naturalness, 24 Sep: the path
    // that put "Hi Not," on a real account's outreach 17-21 Sep), so an
    // override of "Not" or "N/A" counted as a name, did not block the launch
    // and reached the backend as the recipient's firstName. Now it resolves to
    // no name, which blocks a [NAME] script until a real name is typed.
    const injectionFirstName = recipient.hasManualOverride
      ? (safeGreetingFirstName(recipient.firstNameOverride) || '')
      : contactIsConfirmed
        ? extractFirstName(resolvedContactName)
        : '';
    const usesNameVariable = scriptUsesNameVariable(script);
    const requiresName = usesNameVariable && !injectionFirstName;
    const requiresPersonalization = Boolean(
      hasMultipleRecipientIdentities
      && !usesNameVariable
      && hasUnresolvableLiteralGreeting(script)
    );
    const isBlocking = Boolean(
      recipient.requiresContactResolution || requiresName || requiresPersonalization
    );

    return {
      ...recipient,
      rowIndex: property.rowIndex,
      address: property.address || property.propertyAddress || `Property ${propertyIndex + 1}`,
      contactName: resolvedContactName,
      injectionFirstName,
      firstName: injectionFirstName,
      injectionSource: recipient.hasManualOverride
        ? 'manual'
        : injectionFirstName
          ? recipient.source
          : 'missing',
      usesNameVariable,
      requiresName,
      requiresPersonalization,
      isBlocking,
      source: !contactIsConfirmed && resolvedContactName && !recipient.hasManualOverride
          ? 'unconfirmed_contact'
          : recipient.source
    };
  });
}

export function findBlockingRecipientVariableIssue(reviews = []) {
  return reviews.find(review => review.isBlocking) || null;
}
